/* Renders a lesson body — an array of structured blocks — into components.

   The block vocabulary mirrors the content's Markdown directives:

     { p: 'text' }                      paragraph (inline markdown allowed)
     { h: 'text' }                      subheading
     { code: '...', lang, file }        code block with optional filename bar
     { block: 'notice', label, body }   notice | check | try | watch | lookup | submit
     { tracks: { js: [...], py: [...] } } per-language variant — see TrackBlock
     { video: { title, src } }  screencast; a placeholder until `src` is set
     { mdn: [{ title, href, note }] }    links to the canonical reference
     { list: [...], ordered: true }     list
     { table: { head, rows } }          comparison table
     { figure: { caption, height } }    placeholder for a diagram or image
     { indexes: { items, kind, variable, caption } }  an array or string, its indexes
     { coercion: { left, right, op, steps } }  how == converts each side, in steps
*/

import s from './LessonBody.module.css';

const cx = (...v) => v.filter(Boolean).join(' ');
import { Prose, CodeBlock, Block, Kicker } from '../design/primitives.jsx';
import { inline } from '../content/inline.jsx';
import RepoPanel from './RepoPanel.jsx';
import { useTrack, TRACKS } from '../lib/track.jsx';
import { useHookScore } from '../lib/hook.jsx';
import { slug, beatId } from '../lib/sections.js';
import { useState, createContext, useContext } from 'react';

/* Only the hook's answers route the page. A procedural lesson's :::recall
   tests the LAST lesson and its :::step checks test this one piece by piece;
   letting either report would fold scaffolds on evidence about something else. */
const RoutesScore = createContext(false);

function Table({ head, rows }) {
  return (
    <div className={s.tableWrap}>
      <table className={s.table}>
        <thead><tr>{head.map((h, i) => <th key={i}>{inline(h)}</th>)}</tr></thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>{r.map((cell, j) => <td key={j}>{inline(cell)}</td>)}</tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* An array or a string, drawn as two rows: each index above the element (or
   character) at it. When a variable holds it, the variable's name points at
   the table. Only the indexes are coloured (Nick chose this, 2026-10-25). */
function Indexes({ items, kind, variable, caption }) {
  return (
    <figure className={s.indexes}>
      <div className={s.indexesRow}>
        {variable && <>
          <code className={s.indexesVar}>{variable}</code>
          <span className={s.indexesArrow} aria-hidden="true" />
        </>}
        <div className={s.indexesScroll}>
          <table className={s.indexesTable}>
            <tbody>
              <tr><th scope="row">index</th>{items.map((_, i) => <td key={i} className={s.indexesIdx}>{i}</td>)}</tr>
              <tr><th scope="row">{kind}</th>{items.map((v, i) => <td key={i}>{v}</td>)}</tr>
            </tbody>
          </table>
        </div>
      </div>
      {caption && <figcaption className={s.indexesCaption}>{inline(caption)}</figcaption>}
    </figure>
  );
}

/* How a loose comparison converts each side (Nick's design, 2026-10-26).
   Each side's values flow toward the operator: the left side reads left to
   right, the right side right to left, so the originals sit at the outer
   edges and the values finally compared meet in the middle. The small number
   over an arrow is the step it happens in. */
function Coercion({ left, right, op, steps }) {
  const arrow = (n, dir, key) => (
    <span key={key} className={s.coerceArrow} aria-hidden="true">
      <span className={s.coerceStep}>{n}</span>{dir}
    </span>
  );
  const chain = (values, nums, dir) => values.flatMap((v, i) => [
    ...(i ? [arrow(nums[i - 1], dir, 'a' + i)] : []),
    <code key={'v' + i} className={cx(s.coerceValue, i === values.length - 1 && s.coerceFinal)}>{v}</code>,
  ]);
  const said = side => side.map((v, i) => i ? `step ${steps[side === left ? 'left' : 'right'][i - 1]}: ${v}` : v).join(', ');
  return (
    <figure className={s.coerce} aria-label={`Left side: ${said(left)}. Right side: ${said(right)}. Then ${left.at(-1)} ${op} ${right.at(-1)}.`}>
      <div className={s.coerceRow}>
        {chain(left, steps.left, '→')}
        <span className={s.coerceOp}>{op}</span>
        {chain(right, steps.right, '←').reverse()}
      </div>
    </figure>
  );
}

/* The student picked a language. The page is in that language.
 *
 * This used to render tabs for every available track, which was wrong: it
 * reminded a Python student on every page that JavaScript and C# exist, and
 * invited them to browse languages they are not learning. bounded-flexibility.md
 * is explicit that choice is a delivery mechanism, not a menu — the point is
 * that the concept lands in the language they are building a career in.
 *
 * The choice is made once, globally (see the control in the top bar), and every
 * track block on every page follows it. Where a lesson has no variant for the
 * chosen language, the default is shown and labelled, because a missing
 * translation should be visible rather than silently substituted.
 */
function TrackBlock({ tracks }) {
  const [track] = useTrack();
  const chosen = tracks[track];
  const fallbackId = Object.keys(tracks)[0];
  const body = chosen || tracks[fallbackId];
  if (!body) return null;

  const label = TRACKS.find(t => t.id === (chosen ? track : fallbackId))?.label;

  return (
    <div className={s.track}>
      {!chosen && (
        <div className={s.trackFallback}>
          Shown in {label} — this lesson has no {TRACKS.find(t => t.id === track)?.label} version yet.
        </div>
      )}
      <Blocks blocks={body} />
    </div>
  );
}

/* A screencast slot. With no `src` it renders the placeholder frame, so a
   lesson can be written and reviewed before anything has been recorded — and
   the gap is visible rather than silently missing. */
function Video({ title, src, caption }) {
  return (
    <figure className={s.video}>
      <div className={s.videoFrame}>
        {src
          ? <iframe src={src} title={title} loading="lazy" allowFullScreen
                    referrerPolicy="strict-origin-when-cross-origin" />
          : <>
              <span className={s.playMark} aria-hidden="true">▶</span>
              <span className={s.videoStub}>[ screencast — not recorded yet ]</span>
            </>}
      </div>
      <figcaption className={s.videoMeta}>
        <span className={s.videoTitle}>{title}</span>
        {caption && <span>{inline(caption)}</span>}
      </figcaption>
    </figure>
  );
}

/* Links out to the canonical reference. We teach the idea; MDN stays the
   source of truth for the full surface area. */
function Mdn({ items }) {
  return (
    <aside className={s.mdn}>
      <Kicker spacing="tight">Read on MDN</Kicker>
      <ul className={s.mdnList}>
        {items.map((it, i) => (
          <li key={i} className={s.mdnItem}>
            <a className={s.mdnLink} href={it.href} target="_blank" rel="noreferrer">{it.title}</a>
            {it.note && <p className={s.mdnNote}>{inline(it.note)}</p>}
          </li>
        ))}
      </ul>
    </aside>
  );
}

/* Items the student answers inline. The opening of a lesson has to be something
   they DO — "write it down" is not an attempt in a medium where nothing catches
   it, and it cannot route anything either. */
const norm = v => String(v).trim().toLowerCase().replace(/\s+/g, ' ').replace(/[;]$/, '');
/* A multi-line answer — a program's output, one line per line (`lines:` on the
   item). Each line compared as above; blank lines and the ends of lines do not
   count, because the terminal does not show them either. */
const normLines = v => String(v).split(/\r?\n/).map(norm).filter(Boolean).join('\n');

/* "I don't know" (D-040) is an honest miss: it routes like a wrong answer, so
   a lucky guess is not the only way to say "I have nothing" — and a lucky
   guess is what folds away support a student still needs. It shows the rule
   but not the `miss` note, which names a wrong turn they did not take. */
function AskItem({ item, onResult }) {
  const [value, setValue] = useState('');
  const [result, setResult] = useState(null);
  const [unknown, setUnknown] = useState(false);
  const check = (answer = value) => {
    const n = item.lines ? normLines : norm;
    const ok = (item.accept || []).some(a => n(a) === n(answer));
    setValue(String(answer));
    setResult(ok);
    onResult(ok);
  };
  const dontKnow = () => { setUnknown(true); setResult(false); onResult(false); };
  /* A closed question is buttons (D-041); the one picked stays marked. */
  const choices = item.choices?.map(String);
  return (
    <div className={cx(s.askItem, item.question && s.askProse, result === true && s.askRight, result === false && s.askWrong)}>
      {/* `question:` is prose, full width, in the body face; `code:` is a
          snippet in the code face (D-041). A sentence in a code chip read as
          code. */}
      {item.question
        ? <p className={s.askQuestion}>{inline(item.question)}</p>
        : <code className={s.askCode}>{item.code}</code>}
      <div className={s.askAnswer}>
        {choices ? (
          <div className={s.askChoices} role="group" aria-label={`Answer for ${item.code || item.question}`}>
            {choices.map(c => (
              <button key={c} type="button" disabled={result !== null} onClick={() => check(c)}
                      className={cx(s.askChoice, result !== null && !unknown && norm(c) === norm(value) && s.askPicked)}>
                {c}
              </button>
            ))}
          </div>
        ) : item.lines ? (
          <textarea className={cx(s.askInput, s.askLines)} value={value} disabled={result !== null}
                    rows={item.lines} spellCheck={false}
                    placeholder="one line of output per line" aria-label={`Answer for ${item.code || item.question}`}
                    onChange={e => setValue(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && value.trim() && result === null) check(); }} />
        ) : (
          <input className={s.askInput} value={value} disabled={result !== null}
                 placeholder="your answer" aria-label={`Answer for ${item.code || item.question}`}
                 onChange={e => setValue(e.target.value)}
                 onKeyDown={e => { if (e.key === 'Enter' && value.trim() && result === null) check(); }} />
        )}
        {result === null
          ? <>
              {!choices && <button type="button" className={s.askCheck} disabled={!value.trim()} onClick={() => check()}>Check</button>}
              <button type="button" className={s.askIdk} onClick={dontKnow}>I don't know</button>
            </>
          : <span className={cx(s.askVerdict, result ? s.askVRight : s.askVWrong)}>
              {result ? 'right' : unknown ? 'noted' : 'not yet'}
            </span>}
      </div>
      {/* Feedback belongs AT the question, not in a block below it. On a miss
          the misconception comes first and the rule second; on a hit only the
          rule, because naming a wrong turn somebody did not take is noise. */}
      {result === false && !unknown && item.miss && <p className={s.askMiss}>{inline(item.miss)}</p>}
      {result !== null && <p className={s.askWhy}>{inline(item.why)}</p>}
    </div>
  );
}

function Ask({ items }) {
  const { report: reportScore } = useHookScore();
  const routes = useContext(RoutesScore);
  const report = routes ? reportScore : () => {};
  const [results, setResults] = useState({});

  const record = (i, ok) => {
    const next = { ...results, [i]: ok };
    setResults(next);
    if (Object.keys(next).length === items.length) {
      report(Object.values(next).filter(Boolean).length, items.length);
    }
  };

  const done = Object.keys(results).length === items.length;
  const got = Object.values(results).filter(Boolean).length;

  return (
    <div className={s.ask}>
      {items.map((it, i) => <AskItem key={i} item={it} onResult={ok => record(i, ok)} />)}
      {done && (
        <p className={s.askScore}>
          <strong>{got} of {items.length}.</strong>{' '}
          {/* No promises about what the page will do. It used to say the worked
              examples "are folded up", which is true only in the lessons that
              have one and is invisible until you scroll to it. A folded section
              explains itself where it sits. */}
          {got === items.length ? 'You have this.' : 'Worth reading the next part carefully.'}
        </p>
      )}
    </div>
  );
}

/* Support an expert does not need. Folded, never removed. `demo` starts folded
   regardless — How this course works uses one so a student can click a real
   folded section instead of reading about one (D-040). */
function Scaffold({ label, body, demo }) {
  const { confident } = useHookScore();
  const [open, setOpen] = useState(false);
  const folded = (confident || demo) && !open;

  if (folded) {
    return (
      <button type="button" id={label ? `s-${slug(label)}` : undefined}
              className={s.scaffoldFolded} onClick={() => setOpen(true)}>
        <span className={s.scaffoldKicker}>Folded</span>
        <span>{label || 'Worked examples'} — you did not need these. Open anyway.</span>
      </button>
    );
  }
  return <div id={label ? `s-${slug(label)}` : undefined} className={s.scaffold}><Blocks blocks={body} /></div>;
}

/* The beats of both lesson shapes (05-module-structure rule 7b). Named rather
   than generic because the rhythm is the point: page-anatomy.md — "a student
   who learns the rhythm in week 2 never hunts again." */
const BEAT_TITLE = {
  recall: 'Before you start',
  hook: 'Try this first',
  anticipated: 'What most people answer',
  outcomes: "What you'll be able to do",
  compare: 'The comparison',
  revisit: 'Back to the opening',
  apply: 'Apply it',
  summary: 'The things to remember',
};

/* Beats that exist for the instructor and are never rendered to a student.
   `outcomes` is planning information — it belongs in the course documents, and
   on the page it is noise. `anticipated` was the wrong-turn commentary, now
   merged into each question's `miss`. Both stay in the content files and are
   surfaced by `node content/outline.mjs`. */
const INSTRUCTOR_ONLY = new Set(['outcomes', 'anticipated']);

function Beat({ beat, label, body, n }) {
  if (INSTRUCTOR_ONLY.has(beat)) return null;
  const title = beat === 'step' ? `Step ${n}` : BEAT_TITLE[beat] || beat;
  return (
    <section id={beatId(beat, n)} className={cx(s.beat, s[beat])} aria-label={title}>
      {beat === 'step' && label ? (
        <div className={s.stepHead}>
          <span className={s.stepNum} aria-hidden="true">{n}</span>
          <h2 className={s.stepTitle}>{inline(label)}</h2>
        </div>
      ) : (
        <div className={s.beatHead}>
          <span>{title}</span>
          {label && <span className={s.beatLabel}>{label}</span>}
        </div>
      )}
      <div className={s.beatBody}>
        <RoutesScore.Provider value={beat === 'hook'}><Blocks blocks={body} /></RoutesScore.Provider>
      </div>
    </section>
  );
}

/* A block is a source file — numbered — when it names a file and is in a
   programming language. Terminal sessions and program output are not files. */
const SOURCE = new Set(['js', 'javascript', 'mjs', 'ts', 'py', 'python', 'cs', 'csharp', 'rb', 'ruby']);

export function Blocks({ blocks }) {
  if (!blocks) return null;
  const list = Array.isArray(blocks) ? blocks : [blocks];
  return list.map((b, i) => {
    if (typeof b === 'string') return <Prose key={i}>{inline(b)}</Prose>;
    if (b.p != null) return <Prose key={i}>{inline(b.p)}</Prose>;
    if (b.h != null) return <h3 key={i} id={`s-${slug(b.h)}`} className={s.h}>{inline(b.h)}</h3>;
    if (b.code != null) return <CodeBlock key={i} filename={b.file} numbered={!!b.file && SOURCE.has(b.lang)}>{b.code}</CodeBlock>;
    if (b.beat) return <Beat key={i} {...b} />;
    if (b.ask) return <Ask key={i} items={b.ask.items} />;
    if (b.scaffold) return <Scaffold key={i} {...b.scaffold} />;
    if (b.tracks) return <TrackBlock key={i} tracks={b.tracks} />;
    if (b.video) return <Video key={i} {...b.video} />;
    if (b.repo) return b.repo.org
      ? <div key={i} className={s.repo}><RepoPanel {...b.repo} /></div>
      : <p key={i}><em>The repository is not published yet.</em></p>;
    if (b.mdn) return <Mdn key={i} items={b.mdn} />;
    if (b.block != null) return (
      <Block key={i} kind={b.block} label={b.label}><Blocks blocks={b.body} /></Block>
    );
    if (b.list) {
      const Tag = b.ordered ? 'ol' : 'ul';
      return (
        <Tag key={i} className={s.list}>
          {b.list.map((it, j) => <li key={j}>{inline(it)}</li>)}
        </Tag>
      );
    }
    if (b.table) return <Table key={i} {...b.table} />;
    if (b.indexes) return <Indexes key={i} {...b.indexes} />;
    if (b.coercion) return <Coercion key={i} {...b.coercion} />;
    if (b.figure) return (
      <div key={i} className={s.figure} style={{ height: b.figure.height || 210 }}>
        [ {b.figure.caption} ]
      </div>
    );
    return null;
  });
}

export default Blocks;
